import { V2App } from '@wix/ambassador-app-service-webapp/types';
import { AppData, AppDefinitionId } from '@wix/document-services-types';
import {
  isDescriptorExists,
  getAppActions as getBusinessAppActions,
  getAppPremiumBannerInfo as getBusinessAppPremiumBannerInfo,
} from './services/platform/platform';
import {
  HELP_ARTICLE_IDS,
  LITE_MODE_FILTERED_APPS,
} from './constants/constants';
import {
  DealerSegmentApp,
  loadSegmentApps,
  transformSegmentApps,
} from './services/dealer/dealer.segmentApps';
import {
  loadDealerAppsData,
  transformDealerAppsData,
} from './services/dealer/dealer.manageApp';
import { DealerAppPageData } from './services/dealer/dealer.types';
import {
  AppPremiumBannerInfo,
  AppActions,
} from './services/platform/platform.types';
import {
  compareAppsByAppManagerPredefinedList,
  getAppData,
} from './services/appMarket/appMarket.utils';
import {
  AppMarketAddAppParams,
  RegularAppData,
} from './services/appMarket/appMarket.types';
import { App, AppManagerApiDependencies } from '../../types/appManager';
import { getMarketListingApps } from './services/getMarketListingApps/getMarketListingApps';

export interface AppManagerPrivateAPI {
  metaSiteInstance: string;
  hasInstalledApps: boolean;
  availableAppsToUpdate: string[];
  isClassicEditor?: boolean;

  addApp: (params: AppMarketAddAppParams) => void;
  openAppMarket: (openMarketOrigin: string) => void;
  openHelpCenter: (helpId?: string, props?: object) => void;
  normalizeApps: (apps: V2App[]) => RegularAppData[];
  getAppActions: (app: App) => Promise<AppActions>;
  checkPermissions: (permissions: string[]) => boolean;
  checkIsDescriptorExists: (appDefId: string) => boolean;
  loadAndTransformSegmentApps: () => Promise<DealerSegmentApp[]>;
  loadAndTransformAppPageOverrides: () => Promise<
    Record<string, DealerAppPageData>
  >;
  queryMarketListingApps: (locale: string) => Promise<App[]>;
  openBusinessManager: (appDefId: string, path: string) => Promise<void>;
  getAppPremiumBannerInfo: (
    appDefId: string,
  ) => Promise<AppPremiumBannerInfo | null>;
  checkAppsCompletion: AppManagerApiDependencies['checkAppsCompletion'];
}

export type OpenSettings = ({ path }: { path: string }) => void;

export function getAppManagerPrivateAPI(
  appsList: AppDefinitionId[],
  pendingAppDefIds: Record<string, boolean>,
  userPermissions: Set<string>,
  allAvailableToUpdateApps: AppData[],
  apiDependencies: AppManagerApiDependencies,
): AppManagerPrivateAPI {
  const installedEditorApps = apiDependencies.getInstalledEditorApps();

  const installedEditorAppsIds = installedEditorApps.map(
    (app) => app.appDefinitionId,
  );

  const hasInstalledApps = installedEditorApps.some((app) =>
    appsList.includes(app.appDefinitionId),
  );

  const loadAndTransformSegmentApps = async () => {
    const apps = await loadSegmentApps(apiDependencies.metaSiteInstance);

    return transformSegmentApps(apps, apiDependencies);
  };

  const loadAndTransformAppPageOverrides = async () => {
    const assets = await loadDealerAppsData(apiDependencies.metaSiteInstance);
    return transformDealerAppsData(assets, apiDependencies);
  };

  const queryMarketListingApps = async (locale: string) => {
    return getMarketListingApps(appsList, locale);
  };

  const checkPermissions = (permissions: string[]) => {
    return permissions.every((p) => userPermissions.has(p));
  };

  const availableAppsToUpdate = allAvailableToUpdateApps.reduce(
    (acc, { appDefinitionId }) => {
      if (appDefinitionId && appsList.includes(appDefinitionId)) {
        acc.push(appDefinitionId);
      }
      return acc;
    },
    [] as string[],
  );

  const isPendingApp = (appDefId: string) => !!pendingAppDefIds[appDefId];
  const isAppInstalled = (appDefId: string) =>
    installedEditorAppsIds.includes(appDefId);
  const checkIsDescriptorExists = (appDefId: string) =>
    isDescriptorExists(appDefId, apiDependencies);

  const getAppPremiumBannerInfo = (appDefId: string) =>
    getBusinessAppPremiumBannerInfo(appDefId, apiDependencies);

  const getAppActions = (app: App): Promise<AppActions> =>
    getBusinessAppActions(app, apiDependencies);

  const normalizeApps = (apps: V2App[]) => {
    return apps
      .sort(compareAppsByAppManagerPredefinedList(appsList))
      .map(getAppData(appsList))
      .map((app) => {
        if (!app) {
          return null;
        }

        const shouldFilterApp =
          (apiDependencies.isLiteMode &&
            LITE_MODE_FILTERED_APPS.includes(app.id!)) ||
          !app.isPublished;

        if (shouldFilterApp) {
          return null;
        }

        return {
          ...app,
          isPending: isPendingApp(app.id!),
          isInstalled: isAppInstalled(app.id!),
          isUpdateAvailable: availableAppsToUpdate.includes(app.id!),
        };
      })
      .filter(Boolean) as RegularAppData[];
  };

  const { openAppMarket, addApp, openBusinessManager } = apiDependencies;

  const openHelpCenter = (helpId: string = HELP_ARTICLE_IDS.MY_BUSINESS) => {
    apiDependencies.openHelpCenter(helpId);
  };

  const isClassicEditor = !!apiDependencies.isClassicEditor;
  const metaSiteInstance = apiDependencies.metaSiteInstance;

  const checkAppsCompletion = apiDependencies.checkAppsCompletion;

  return {
    metaSiteInstance,
    hasInstalledApps,
    availableAppsToUpdate,
    isClassicEditor,
    addApp,
    openAppMarket,
    normalizeApps,
    getAppActions,
    openHelpCenter,
    checkPermissions,
    loadAndTransformSegmentApps,
    loadAndTransformAppPageOverrides,
    queryMarketListingApps,
    checkIsDescriptorExists,
    openBusinessManager,
    getAppPremiumBannerInfo,
    checkAppsCompletion,
  };
}
